import { api } from '../../app/services/api';
import { DateTime, Interval } from 'luxon';
import exhaustiveGuard from '@utils/exhaustiveGuard';

export enum MaintenanceFeatures {
  LEO_CLOUD = '*',
  TERMINAL_MODE = 'TM',
}

export const getMaintenanceFeatureLabel = (option: MaintenanceFeatures) => {
  switch (option) {
    case MaintenanceFeatures.LEO_CLOUD:
      return 'LEO Cloud';
    case MaintenanceFeatures.TERMINAL_MODE:
      return 'Terminal Mode';
    default:
      return exhaustiveGuard(option);
  }
};

export const getMaintenanceFeaturesLabels = (
  features: MaintenanceFeatures[],
) => {
  if (features.length === 1) return getMaintenanceFeatureLabel(features[0]);
  if (features.includes(MaintenanceFeatures.LEO_CLOUD))
    return getMaintenanceFeatureLabel(MaintenanceFeatures.LEO_CLOUD);
  return features
    .map((feature) => getMaintenanceFeatureLabel(feature))
    .join(', ');
};

export type MaintenanceInfo = {
  start_date: string | null;
  end_date: string | null;
  reason: string | null;
  warning_lead_hours: number | null;
  features: MaintenanceFeatures[];
};

export const adminApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getMaintenanceInfo: builder.query<MaintenanceInfo, void>({
      query: () => ({
        url: 'admin/maintenance/info',
        method: 'GET',
      }),
      providesTags: ['Maintenance'],
    }),
    setMaintenanceInfo: builder.mutation<unknown, MaintenanceInfo>({
      query: (body) => ({
        url: 'admin/maintenance/info',
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Maintenance'],
    }),
  }),
});

export const { useGetMaintenanceInfoQuery, useSetMaintenanceInfoMutation } =
  adminApi;

export function useMaintenanceInfo() {
  return useGetMaintenanceInfoQuery(undefined, {
    pollingInterval: 90000,
    refetchOnReconnect: true,
  });
}

export function processMaintenanceInfo(info: MaintenanceInfo) {
  const warningLeadHours = info.warning_lead_hours ?? 48;
  if (info.start_date === null || info.end_date === null) {
    return null;
  }
  const startDate = DateTime.fromISO(info.start_date);
  const endDate = DateTime.fromISO(info.end_date);
  const maintenanceInterval = Interval.fromDateTimes(startDate, endDate);

  const formattedStartDate = startDate.toLocaleString(DateTime.DATETIME_FULL);
  const formattedEndDate = endDate.toLocaleString(DateTime.DATETIME_FULL);
  const formattedMaintenanceInterval = maintenanceInterval.toLocaleString(
    DateTime.DATETIME_FULL,
  );

  const warningStartDate = startDate.minus({ hours: warningLeadHours });
  const warningInterval = Interval.fromDateTimes(warningStartDate, startDate);

  const now = DateTime.now();

  const isWarningActive = warningInterval.contains(now);
  const isMaintenanceActive = maintenanceInterval.contains(now);
  const timeUntilWarningStart = warningStartDate
    .diff(now, 'seconds')
    .toObject();
  const timeUntilMaintenanceStart = startDate.diff(now, 'seconds').toObject();
  const timeUntilEnd = endDate.diff(now, 'seconds').toObject();

  const isLeoCloudMaintenance = info.features.includes(
    MaintenanceFeatures.LEO_CLOUD,
  );
  const isTerminalModeMaintenance = info.features.includes(
    MaintenanceFeatures.TERMINAL_MODE,
  );

  return {
    startDate,
    endDate,
    maintenanceInterval,
    formattedStartDate,
    formattedEndDate,
    formattedMaintenanceInterval,
    isWarningActive,
    isMaintenanceActive,
    timeUntilWarningStart,
    timeUntilMaintenanceStart,
    timeUntilEnd,
    isLeoCloudMaintenance,
    isTerminalModeMaintenance,
  };
}
